import * as constants from './constants'
import { colours, link_plp_category_padding_x, TRANSITION_FAST } from './constants'
import { makeId } from '@kissui/helpers/src/utils'

class SliderMenu {
    /**
     * @param {HTMLElement} element
     * @param {Object} options
     * @param {Number} [options.slidesToScroll=1] options.slidesToScroll of element do slide
     * @param {Number} [options.slidesVisible=1] options.slidesVisible of element to display
     * @param {Boolean} [options.navigationButton=true] options.navigationButton show navigation button
     */

    constructor(element, options = {}) {
        this.boundNextClick = this.onNextClick.bind(this)
        this.boundPrevClick = this.onPrevClick.bind(this)
        this.boundLinkClick = this.onLinkClick.bind(this)
        this.boundTouchEnd = this.onTouchEnd.bind(this)
        this.disableAutoScroll = options.disableAutoScroll === 'true' || false
        this._entriesBounds = null

        return new Promise(resolve => {
            this._element = element
            this._slides = [].slice.call(element.children)
            this._options = Object.assign(
                {},
                {
                    id: 'uuid',
                    last_known_scroll_position: 0,
                    ticking: false,
                    navBarTravelDirection: '',
                    navBarTravelling: false,
                    navBarTravelDistance: 150,
                    scrollAmount: 220
                },
                options
            )

            this.buildIHM()
            resolve(this)

            // Wait for CSS animation before setting the indicator
            setTimeout(() => {
                this.setOverflowAttribute()
                this.initializeIndicatorPosition()
            }, 0)
        })
    }

    /**
     * @returns {HTMLElement}
     */
    get element() {
        return this._element
    }

    // MAIN BUILDER
    buildIHM() {
        this._root = this.createElement('nav', constants.root)
        this._container = this.createElement('ul', constants.container)
        this._pnIndicator = null
        this._containerSlidesContent = null

        if (this.hasContainer()) {
            this._containerSlidesContent = this.createElement('div', constants.slidesContainer)
        }

        this.wrapItems()
        this._root.appendChild(this._container)

        if (this._containerSlidesContent) {
            this._element.appendChild(this._containerSlidesContent)
        }

        // Insert the slider between Arrows for accessibility keyboard navigation
        this._element.insertBefore(this._root, this._element.childNodes[2])

        this.createIndicator()
    }

    hasContainer() {
        if (document.querySelector('.slider-container')) return true

        return false
    }

    wrapItems() {
        const keys = []
        this._slides.map(child => {
            const randId = makeId(5)

            if (this.hasContainer()) {
                if (child.classList.contains('slider-container')) {
                    if (child.children) {
                        if (child.children.length) {
                            ;[...child.children].forEach((el, i) => {
                                if (i !== 0) {
                                    el.style.display = 'none'
                                }

                                el.id = keys[i]

                                this._containerSlidesContent.appendChild(el)
                            })
                        }
                    }
                    child.remove()
                }
            }

            if (!child.classList.contains('pn-Advancer')) {
                const item = this.createElement('div', constants.item)
                item.appendChild(child)
                item.setAttribute('data-inner-content-id', randId)
                keys.push(randId)

                this._container.appendChild(item)
            }
        })
    }

    createElement(tag, className) {
        let div = document.createElement(tag)
        div.setAttribute('class', className)
        return div
    }

    // COMPONENT ROOT "ELEMENT" COMPOSER
    createIndicator() {
        this.elements = []
        this._pnIndicator = this.createElement('div', constants.pnIndicator)
        this._container.appendChild(this._pnIndicator)

        this.elements = Array.from(this._container.querySelectorAll('nb-link a'))
        // Handle setting the currently active link
        this.elements.forEach(element => {
            element.addEventListener('click', this.boundLinkClick)
        })

        const pnAdvancerLeft = document.getElementById(`${constants.prev}-${this._options.id}`)
        if (pnAdvancerLeft) {
            pnAdvancerLeft.addEventListener('click', this.boundPrevClick)
        }

        const pnAdvancerRight = document.getElementById(`${constants.next}-${this._options.id}`)
        if (pnAdvancerRight) {
            pnAdvancerRight.addEventListener('click', this.boundNextClick)
        }

        if (this._root) {
            this._container.addEventListener('touchend', this.boundTouchEnd)
        }
    }

    onLinkClick(e) {
        this.goToElement(e.currentTarget)
    }

    goToSlide(index) {
        this.goToElement(this._slides[index])
    }

    showContent(id) {
        const slidesContainer = document.querySelector(`.${constants.slidesContainer}`)

        if (slidesContainer.children) {
            ;[...slidesContainer.children].forEach(el => (el.style.display = 'none'))
        }

        document.getElementById(id).style.display = 'block'
    }

    goToElement(element) {
        if (this.hasContainer()) {
            const contentId = element
                .closest('[data-inner-content-id]')
                .getAttribute('data-inner-content-id')

            this.showContent(contentId)
        }

        this.deselectAllLinkBut(element)
        element.setAttribute('aria-current', 'true')

        if (!element || !this._entriesBounds) {
            return
        }

        let distance = 0

        for (const entry of this._entriesBounds) {
            if (entry.target.getAttribute('data-href') === element.getAttribute('data-href')) {
                break
            } else {
                distance = distance + entry.boundingClientRect.width + link_plp_category_padding_x
            }
        }

        if (!this.disableAutoScroll) {
            this._root.scroll({ left: distance, behavior: 'smooth' })
        }
    }

    deselectAllLinkBut(element) {
        this.elements.forEach(el => {
            if (el !== element) {
                el.setAttribute('aria-current', 'false')
            }
        })
    }

    sideScroll(element, direction, speed, distance, step) {
        this.setOverflowAttribute()

        let scrollAmount = 0
        let slideTimer = setInterval(function () {
            if (direction === 'left') {
                element.scrollLeft -= step
            } else {
                element.scrollLeft += step
            }
            scrollAmount += step
            if (scrollAmount >= distance) {
                window.clearInterval(slideTimer)
            }
        }, speed)
        return { slideTimer }
    }

    onPrevClick() {
        this.sideScroll(this._root, 'left', 25, this._options.scrollAmount, 10)
        setTimeout(() => {
            this.setOverflowAttribute()
        }, TRANSITION_FAST)
    }

    onNextClick() {
        this.sideScroll(this._root, 'right', 25, this._options.scrollAmount, 10)
        setTimeout(() => {
            this.setOverflowAttribute()
        }, TRANSITION_FAST)
    }

    onTouchEnd() {
        this.setOverflowAttribute()
    }

    initializeIndicatorPosition() {
        const selected = this._root.querySelector('[aria-current="true"]')

        // Set the indicator
        if (selected) {
            this.moveIndicator(selected, colours[0])
        }
    }

    determineOverflow(content, container) {
        let containerMetrics = container.getBoundingClientRect()
        let containerMetricsRight = Math.floor(containerMetrics.right)
        let containerMetricsLeft = Math.floor(containerMetrics.left)

        let contentMetrics = content.getBoundingClientRect()
        let contentMetricsRight = Math.floor(contentMetrics.right)
        let contentMetricsLeft = Math.floor(contentMetrics.left)

        if (
            containerMetricsLeft > contentMetricsLeft &&
            containerMetricsRight < contentMetricsRight
        ) {
            return 'both'
        } else if (contentMetricsLeft < containerMetricsLeft) {
            return 'left'
        } else if (contentMetricsRight > containerMetricsRight) {
            return 'right'
        } else {
            return 'none'
        }
    }

    moveIndicator(item, color) {
        if (!this._pnIndicator) {
            return
        }

        let textPosition = item.getBoundingClientRect()
        let container = this._container.getBoundingClientRect().left
        let distance = textPosition.left - container

        this._pnIndicator.style.transform =
            'translateX(' +
            (distance + this._container.scrollLeft + link_plp_category_padding_x) +
            'px) scaleX(' +
            (textPosition.width - link_plp_category_padding_x * 2) * 0.01 +
            ')'

        this._root.scrollLeft = Math.floor(
            distance + this._container.getBoundingClientRect().left / 2
        )

        if (color) {
            this._pnIndicator.style.backgroundColor = color
        }
    }

    onResize() {
        this.setOverflowAttribute()
        this.initializeIndicatorPosition()
    }

    clearListeners() {
        this.elements.forEach(element => {
            element.removeEventListener('click', this.boundLinkClick)
        })

        if (typeof document !== 'undefined') {
            const pnAdvancerLeft = document.getElementById(`${constants.prev}-${this._options.id}`)
            if (pnAdvancerLeft) {
                pnAdvancerLeft.removeEventListener('click', this.boundPrevClick)
            }

            const pnAdvancerRight = document.getElementById(`${constants.next}-${this._options.id}`)
            if (pnAdvancerRight) {
                pnAdvancerRight.removeEventListener('click', this.boundNextClick)
            }
        }
    }

    kill() {
        this.clearListeners()
    }

    setOverflowAttribute() {
        this._element.setAttribute(
            'data-overflowing',
            this.determineOverflow(this._container, this._root)
        )

        this.observe()
    }

    observe() {
        if (!('IntersectionObserver' in window)) {
            return false
        }

        this._entriesBounds = []

        // Using IntersectionObserver to prevent reflow while getting boundingClientRect
        const observer = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    this._entriesBounds.push({
                        target: entry.target,
                        intersection:
                            entry.boundingClientRect.right <=
                                window.innerWidth - link_plp_category_padding_x &&
                            entry.boundingClientRect.left >= link_plp_category_padding_x,
                        boundingClientRect: entry.boundingClientRect
                    })
                }

                observer.disconnect()
            },
            { root: this._container }
        )

        this._slides.map(element => observer.observe(element))
    }

    isInViewport(element) {
        if (!this._entriesBounds) {
            return false
        }

        const entry = this._entriesBounds.find(entryBound => element === entryBound.target)

        return entry.intersection
    }
}

export default SliderMenu
